import React from 'react';
import { OptImg } from '../ui/OptImg';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';

export const isExternal = (href = '') => /^(https?:)?\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
export const inPreviewFrame = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/preview-frame');

export const SmartLink = ({ to, children, className = '', ...rest }) => {
  if (!to) return <span className={className} {...rest}>{children}</span>;
  if (isExternal(to)) return <a href={to} target={to.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className={className} {...rest}>{children}</a>;
  return <Link to={to} className={className} {...rest}>{children}</Link>;
};

const BTN_STYLES = {
  primary: 'bg-[color:var(--site-dark)] text-white hover:opacity-90',
  secondary: 'border border-current text-[color:var(--site-dark)] hover:bg-[color:var(--site-dark)] hover:text-white',
  light: 'bg-white text-neutral-900 hover:bg-neutral-100',
  outline_light: 'border border-white/40 text-white hover:bg-white hover:text-neutral-900',
  accent: 'bg-[color:var(--site-accent)] text-white hover:opacity-90',
  link: 'underline underline-offset-4 text-current px-0 py-0',
};

// Coded pages use shadcn <Button>: h-9, px-4, radius from theme (0), shadow, bg-primary. Use with CmsButton className + icon={false}.
// NOTE: Colors are NOT included here; pass them per-button to avoid CSS cascade conflicts with !important overrides.
export const SHADCN_BTN = 'inline-flex items-center justify-center whitespace-nowrap h-9 !px-4 !py-2 !rounded-md shadow';
export const SHADCN_BTN_PRIMARY = '!bg-primary !text-primary-foreground hover:!bg-primary/90';

export const CmsButton = ({ button, variant, className = '', testId, icon = true }) => {
  if (!button?.text) return null;
  const style = BTN_STYLES[variant || button.style || 'primary'] || BTN_STYLES.primary;
  return (
    <SmartLink to={button.link || '#'} data-testid={testId}
      className={`inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium tracking-wide uppercase transition-all rounded-[var(--site-btn-radius)] ${style} ${className}`}>
      {button.text}
      {icon && <ArrowRight className="w-4 h-4" />}
    </SmartLink>
  );
};

export const CmsImage = ({ image, className = '', fallbackAlt = '', loading = 'lazy', style }) => {
  const url = typeof image === 'string' ? image : image?.url;
  if (!url) return null;
  const crop = typeof image === 'object' && (image.aspect || image.focal) ? { aspectRatio: image.aspect ? image.aspect.replace(':', '/') : undefined, objectPosition: image.focal || undefined } : {};
  const alt = typeof image === 'object' && image?.decorative ? '' : (typeof image === 'object' && image?.alt_text) || fallbackAlt;
  return <OptImg src={url} alt={alt} className={`rounded-[var(--site-radius)] ${image?.aspect ? 'object-cover' : ''} ${className}`} loading={loading} style={{ ...crop, ...style }} />;
};

const looksLikeHtml = (s = '') => /<[a-z][\s\S]*>/i.test(s);

export const RichText = ({ html, className = '' }) => {
  if (!html) return null;
  if (!looksLikeHtml(html)) {
    return (
      <div className={`space-y-4 ${className}`}>
        {html.split(/\n{2,}/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
      </div>
    );
  }
  return <div className={`cms-prose ${className}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
};

const BG = {
  white: 'bg-white text-neutral-900',
  light: 'bg-[color:var(--site-surface)] text-neutral-900',
  dark: 'bg-[color:var(--site-dark)] text-white',
  brand: 'bg-[color:var(--site-accent)] text-white',
  brand_light: 'bg-amber-50 text-neutral-900',
  brand_bright: 'bg-amber-600 text-white',
  image: 'text-white',
};
const PAD = { none: 'py-0', minimal: 'py-8', tight: 'py-10', compact: 'py-12', medium: 'py-24', normal: 'py-20 lg:py-28', spacious: 'py-28 lg:py-40' };

export const isDarkBg = (style) => ['dark', 'brand', 'brand_bright', 'image'].includes(style?.background);

export const SectionShell = ({ section, children, className = '', container = 'xl', testId }) => {
  const style = section.style || {};
  const vis = section.visibility || {};
  const hideCls = [vis.mobile === false && 'max-sm:hidden', vis.tablet === false && 'sm:max-lg:hidden', vis.desktop === false && 'lg:hidden'].filter(Boolean).join(' ');
  const bgCls = BG[style.background] || BG.white;
  const padCls = PAD[style.padding] || PAD.normal;
  const inner = container === 'none' ? children : <div className={container === 'lg' ? 'container-lg' : 'container-xl'}>{children}</div>;
  return (
    <section id={style.anchor || section.id} data-testid={testId || `section-${section.type}`} data-section-id={section.id}
      className={`relative ${bgCls} ${padCls} ${hideCls} ${className}`}>
      {style.background === 'image' && style.background_image?.url && (
        <div className="absolute inset-0 -z-0">
          <img src={style.background_image.url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-neutral-900/70" />
        </div>
      )}
      <div className="relative z-10">{inner}</div>
    </section>
  );
};

export const SectionHeader = ({ props = {}, style = {}, dark, className = '', titleClass = '' }) => {
  if (!props.eyebrow && !props.title && !props.body) return null;
  const center = style.align === 'center';
  const link = props.header_link?.text ? <SmartLink to={props.header_link.link || '#'} className={`text-sm font-medium link-underline flex-shrink-0 ${dark ? 'text-white' : 'text-neutral-900'}`} data-testid="section-header-link">{props.header_link.text}</SmartLink> : null;
  const block = (
    <div className={`${center ? 'text-center mx-auto' : ''} max-w-3xl ${link || /\bmb-/.test(className) ? '' : 'mb-16'} ${className}`}>
      {props.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{props.eyebrow}</span>}
      {props.title && <h2 className={`mt-4 ${dark ? 'text-white' : 'text-neutral-900'} ${titleClass}`}>{props.title}</h2>}
      {props.body && <RichText html={props.body} className={`mt-5 text-lg ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />}
    </div>
  );
  if (!link) return block;
  return <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">{block}{link}</div>;
};

export const videoEmbed = (url = '') => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}?rel=0` };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}` };
  return url ? { type: 'video', src: url } : null;
};
